import {
  ACTIVE_CLASS,
  ANIMATING_CLASS,
  ENTERING_CLASS,
  EXITING_CLASS,
} from '../shared/constants.js';
import { prefersReducedMotion } from '../shared/browser.js';

let tabSequence = 0;

const getSlideParts = (slide) => ({
  left: slide.querySelector('.tab__content-col--left'),
  card: slide.querySelector('.tab__card .tab__content-col--right'),
});

const getSlideTargets = (parts) => [parts.left, parts.card].filter(Boolean);

const isMobileTabsLayout = () => window.matchMedia('(max-width: 767px)').matches;

const setTabLinkState = (links, activeIndex) => {
  links.forEach((link, index) => {
    const isActive = index === activeIndex;

    link.classList.toggle(ACTIVE_CLASS, isActive);
    link.classList.toggle('w--current', isActive);
    link.setAttribute('aria-selected', String(isActive));
    link.setAttribute('tabindex', isActive ? '0' : '-1');
  });
};

const finishTabChange = (tabs, currentSlide, nextSlide, nextIndex, state) => {
  currentSlide.classList.remove(ACTIVE_CLASS, EXITING_CLASS);
  currentSlide.setAttribute('aria-hidden', 'true');

  nextSlide.classList.remove(ENTERING_CLASS);
  nextSlide.classList.add(ACTIVE_CLASS);

  tabs.classList.remove(ANIMATING_CLASS);
  state.activeIndex = nextIndex;
  state.isAnimating = false;
};

const showTabInstantly = (tabs, links, slides, nextIndex, state) => {
  const currentSlide = slides[state.activeIndex];
  const nextSlide = slides[nextIndex];

  setTabLinkState(links, nextIndex);
  nextSlide.setAttribute('aria-hidden', 'false');
  currentSlide.classList.add(EXITING_CLASS);
  nextSlide.classList.add(ENTERING_CLASS);
  finishTabChange(tabs, currentSlide, nextSlide, nextIndex, state);
};

const showTabWithMobileFade = (
  tabs,
  links,
  currentSlide,
  nextSlide,
  currentParts,
  nextParts,
  nextIndex,
  state
) => {
  const currentTargets = getSlideTargets(currentParts);
  const nextTargets = getSlideTargets(nextParts);

  state.isAnimating = true;
  setTabLinkState(links, nextIndex);
  tabs.classList.add(ANIMATING_CLASS);

  currentSlide.classList.add(EXITING_CLASS);
  nextSlide.classList.add(ENTERING_CLASS);
  nextSlide.setAttribute('aria-hidden', 'false');

  state.gsap.set(nextTargets, {
    autoAlpha: 0,
    xPercent: 0,
  });

  state.timeline?.kill();
  state.timeline = state.gsap.timeline({
    defaults: { overwrite: 'auto' },
    onComplete: () => {
      finishTabChange(tabs, currentSlide, nextSlide, nextIndex, state);

      state.gsap.set([...currentTargets, ...nextTargets], {
        clearProps: 'transform,opacity,visibility',
      });
    },
  });

  state.timeline
    .to(currentTargets, {
      autoAlpha: 0,
      duration: 0.24,
      ease: 'power1.out',
    })
    .to(nextTargets, {
      autoAlpha: 1,
      duration: 0.28,
      ease: 'power1.inOut',
    });
};

const showTab = (tabs, links, slides, nextIndex, state) => {
  const currentIndex = state.activeIndex;

  if (state.isAnimating || nextIndex === currentIndex) {
    return;
  }

  if (!state.gsap || prefersReducedMotion()) {
    showTabInstantly(tabs, links, slides, nextIndex, state);
    return;
  }

  const currentSlide = slides[currentIndex];
  const nextSlide = slides[nextIndex];
  const currentParts = getSlideParts(currentSlide);
  const nextParts = getSlideParts(nextSlide);

  if (isMobileTabsLayout()) {
    showTabWithMobileFade(
      tabs,
      links,
      currentSlide,
      nextSlide,
      currentParts,
      nextParts,
      nextIndex,
      state
    );
    return;
  }

  const enterX = -112;
  const exitX = 12;

  state.isAnimating = true;
  setTabLinkState(links, nextIndex);
  tabs.classList.add(ANIMATING_CLASS);

  currentSlide.classList.add(EXITING_CLASS);
  nextSlide.classList.add(ENTERING_CLASS);
  nextSlide.setAttribute('aria-hidden', 'false');

  state.gsap.set(nextParts.left, { autoAlpha: 0 });
  state.gsap.set(nextParts.card, {
    autoAlpha: 1,
    xPercent: enterX,
  });

  state.timeline?.kill();
  state.timeline = state.gsap.timeline({
    defaults: { overwrite: 'auto' },
    onComplete: () => {
      finishTabChange(tabs, currentSlide, nextSlide, nextIndex, state);

      state.gsap.set([currentParts.card, nextParts.card], {
        clearProps: 'transform,opacity,visibility',
      });
      state.gsap.set([currentParts.left, nextParts.left], {
        clearProps: 'opacity,visibility',
      });
    },
  });

  state.timeline
    .to(currentParts.left, {
      autoAlpha: 0,
      duration: 0.24,
      ease: 'power1.out',
    })
    .to(
      currentParts.card,
      {
        autoAlpha: 0.65,
        duration: 0.62,
        ease: 'power3.out',
        xPercent: exitX,
      },
      0
    )
    .to(
      nextParts.card,
      {
        duration: 0.72,
        ease: 'expo.out',
        xPercent: 0,
      },
      0.04
    )
    .to(
      nextParts.left,
      {
        autoAlpha: 1,
        duration: 0.28,
        ease: 'power1.inOut',
      },
      0.28
    );
};

const handleTabKeydown = (event, tabs, links, slides, state) => {
  const currentIndex = links.indexOf(document.activeElement);

  if (currentIndex < 0) {
    return;
  }

  let nextIndex = currentIndex;

  if (event.key === 'ArrowRight') {
    nextIndex = (currentIndex + 1) % links.length;
  } else if (event.key === 'ArrowLeft') {
    nextIndex = (currentIndex - 1 + links.length) % links.length;
  } else if (event.key === 'Home') {
    nextIndex = 0;
  } else if (event.key === 'End') {
    nextIndex = links.length - 1;
  } else {
    return;
  }

  event.preventDefault();
  links[nextIndex].focus();
  showTab(tabs, links, slides, nextIndex, state);
};

export const initTabs = (tabs, gsap) => {
  if (tabs.dataset.tabsReady === 'true') {
    return;
  }

  const links = Array.from(tabs.querySelectorAll('.tab__link'));
  const slides = Array.from(tabs.querySelectorAll('.content__tab-slide'));

  if (!links.length || links.length !== slides.length) {
    return;
  }

  tabSequence += 1;
  tabs.dataset.tabsReady = 'true';

  const state = {
    activeIndex: 0,
    gsap,
    isAnimating: false,
    timeline: null,
  };
  const tabId = `content-tabs-${tabSequence}`;

  tabs.querySelector('.tabs__menu')?.setAttribute('role', 'tablist');

  slides.forEach((slide, index) => {
    const parts = getSlideParts(slide);

    gsap?.set(parts.left, { autoAlpha: index === 0 ? 1 : 0 });
    gsap?.set(parts.card, { autoAlpha: 1, xPercent: 0 });
  });

  links.forEach((link, index) => {
    const slide = slides[index];
    const linkId = `${tabId}-tab-${index}`;
    const panelId = `${tabId}-panel-${index}`;

    link.id = linkId;
    link.setAttribute('role', 'tab');
    link.setAttribute('aria-controls', panelId);

    slide.id = panelId;
    slide.setAttribute('role', 'tabpanel');
    slide.setAttribute('aria-labelledby', linkId);
    slide.setAttribute('aria-hidden', String(index !== 0));
    slide.classList.toggle(ACTIVE_CLASS, index === 0);

    link.addEventListener('click', (event) => {
      event.preventDefault();
      showTab(tabs, links, slides, index, state);
    });
  });

  tabs.addEventListener('keydown', (event) => {
    handleTabKeydown(event, tabs, links, slides, state);
  });

  setTabLinkState(links, 0);
};
